import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminMarkInvoicePaidRequest } from '@naya/shared'
import { adminApi } from '../adminApi.js'
import type {
  InvoiceListResponse,
  InvoiceRow,
  InvoiceStatus,
} from '../types.js'
import { tenantDetailKey } from './useTenants.js'

export const invoicesListKey = (filters: {
  tenantId?: string | undefined
  status?: string | undefined
}) => ['admin', 'invoices', 'list', filters] as const
export const invoiceDetailKey = (id: string) =>
  ['admin', 'invoices', 'detail', id] as const
export const tenantInvoicesKey = (tenantId: string) =>
  ['admin', 'tenants', tenantId, 'invoices'] as const

interface InvoiceResponse {
  invoice: InvoiceRow
}

interface TenantInvoicesResponse {
  invoices: InvoiceRow[]
  next_cursor: string | null
}

/** Cross-tenant invoice list — the standalone /admin/invoices page. */
export function useInvoicesList(filters: {
  tenantId?: string
  status?: InvoiceStatus | ''
}) {
  const params = new URLSearchParams()
  if (filters.tenantId) params.set('tenant_id', filters.tenantId)
  if (filters.status) params.set('status', filters.status)
  const qs = params.toString()
  return useQuery({
    queryKey: invoicesListKey({
      tenantId: filters.tenantId,
      status: filters.status || undefined,
    }),
    queryFn: () =>
      adminApi.get<InvoiceListResponse>(`/invoices${qs ? `?${qs}` : ''}`),
  })
}

/** Per-tenant invoice list — shown under the tenant detail Invoices tab. */
export function useTenantInvoices(tenantId: string) {
  return useQuery({
    queryKey: tenantInvoicesKey(tenantId),
    queryFn: () =>
      adminApi.get<TenantInvoicesResponse>(`/tenants/${tenantId}/invoices`),
  })
}

export function useInvoice(invoiceId: string) {
  return useQuery({
    queryKey: invoiceDetailKey(invoiceId),
    queryFn: () => adminApi.get<InvoiceResponse>(`/invoices/${invoiceId}`),
  })
}

/** Broadly invalidate invoice-related queries after a state transition. */
function invalidateInvoices(
  qc: ReturnType<typeof useQueryClient>,
  invoiceId: string,
  tenantId?: string,
) {
  void qc.invalidateQueries({ queryKey: invoiceDetailKey(invoiceId) })
  void qc.invalidateQueries({ queryKey: ['admin', 'invoices', 'list'] })
  if (tenantId) {
    void qc.invalidateQueries({ queryKey: tenantInvoicesKey(tenantId) })
    void qc.invalidateQueries({ queryKey: tenantDetailKey(tenantId) })
  }
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { invoiceId: string } & AdminMarkInvoicePaidRequest) => {
      const { invoiceId, ...body } = vars
      return adminApi.post<{
        invoice: InvoiceRow
        next_invoice: InvoiceRow
      }>(`/invoices/${invoiceId}/mark-paid`, body)
    },
    onSuccess: (res, vars) =>
      invalidateInvoices(qc, vars.invoiceId, res.invoice.tenant_id),
  })
}

export function useVoidInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { invoiceId: string; reason: string }) =>
      adminApi.post<InvoiceResponse>(`/invoices/${vars.invoiceId}/void`, {
        reason: vars.reason,
      }),
    onSuccess: (res, vars) =>
      invalidateInvoices(qc, vars.invoiceId, res.invoice.tenant_id),
  })
}
